import { useEffect, useState } from "react";
import { SearchableSelect } from "@/components/shared/SearchableSelect";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import client, { getErrorMessage } from "@/api/client";
import { toast } from "sonner";

const JOB_WORK_TYPES = ["Job Work", "Rental", "Other"];
const SUPPORT_METHODS = ["Permanent Liner", "Temporary Casing"];
const RATE_STRUCTURES = ["Layered", "Flat"];
const SITE_STATUSES = ["Active", "Completed"];

export default function SiteFormDialog({ site, onClose, onSaved }) {
  const isEdit = !!site;
  const [states, setStates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    site_value: site?.label || "",
    client_short_name: site?.client_short_name || "",
    client_name_full: site?.client_name_full || "",
    job_work_type: site?.job_work_type || "Job Work",
    state: site?.state || "",
    site_status: site?.site_status || "Active",
    support_method: site?.support_method || "",
    rate_structure: site?.rate_structure || "",
  });

  useEffect(() => {
    client
      .get("/site-master/states")
      .then((res) => setStates(res.data))
      .catch((err) => toast.error(getErrorMessage(err)));
  }, []);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));
  const isJobWork = form.job_work_type === "Job Work";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.site_value.trim()) return toast.error("Site Value is required");
    if (!form.client_short_name.trim()) return toast.error("Client Short Name is required");
    if (!form.state) return toast.error("State is required");
    setSaving(true);
    try {
      if (isEdit) {
        const payload = {
          site_value: form.site_value.trim(),
          client_short_name: form.client_short_name.trim(),
          client_name_full: form.client_name_full || null,
          state: form.state,
          site_status: form.site_status,
          support_method: isJobWork ? form.support_method || null : null,
          rate_structure: isJobWork ? form.rate_structure || null : null,
        };
        const res = await client.put(`/site-master/sites/${site.id}`, payload);
        toast.success("Site updated");
        onSaved(res.data);
      } else {
        const payload = {
          site_value: form.site_value.trim(),
          client_short_name: form.client_short_name.trim(),
          client_name_full: form.client_name_full || null,
          job_work_type: form.job_work_type,
          state: form.state,
          support_method: isJobWork ? form.support_method || null : null,
          rate_structure: isJobWork ? form.rate_structure || null : null,
        };
        const res = await client.post("/site-master/sites", payload);
        toast.success(`Site created — ${res.data.site_id}`);
        onSaved(res.data);
      }
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="site-form-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit Site — ${site.site_id}` : "Add Site"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Site Value *</label>
              <Input data-testid="site-form-value-input" value={form.site_value} onChange={(e) => set("site_value", e.target.value)} required />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Client Short Name *</label>
              <Input data-testid="site-form-client-short-input" value={form.client_short_name} onChange={(e) => set("client_short_name", e.target.value)} required />
            </div>
            <div className="col-span-2">
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Client Name (Full)</label>
              <Input data-testid="site-form-client-full-input" value={form.client_name_full} onChange={(e) => set("client_name_full", e.target.value)} />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Job Work / Rental Type *</label>
              <Select value={form.job_work_type} onValueChange={(v) => set("job_work_type", v)} disabled={isEdit}>
                <SelectTrigger data-testid="site-form-job-work-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {JOB_WORK_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`site-form-job-work-type-option-${t}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {isEdit && <p className="text-xs text-slate-400 mt-1">Type cannot be changed after creation.</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">State *</label>
              <SearchableSelect testId="site-form-state-select" value={form.state} onValueChange={(v) => set("state", v)} placeholder="Select state"
  options={states.map((s) => ({ value: s.label, label: s.label, testId: `site-form-state-option-${s.code}` }))} />
            </div>
            {isEdit && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1.5 block">Site Status</label>
                <Select value={form.site_status} onValueChange={(v) => set("site_status", v)}>
                  <SelectTrigger data-testid="site-form-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {SITE_STATUSES.map((s) => (
                      <SelectItem key={s} value={s} data-testid={`site-form-status-option-${s}`}>
                        {s}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {isJobWork && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Support Method</label>
                  <Select value={form.support_method || ""} onValueChange={(v) => set("support_method", v)}>
                    <SelectTrigger data-testid="site-form-support-method-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUPPORT_METHODS.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`site-form-support-method-option-${s}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1.5 block">Rate Structure</label>
                  <Select value={form.rate_structure || ""} onValueChange={(v) => set("rate_structure", v)}>
                    <SelectTrigger data-testid="site-form-rate-structure-select">
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {RATE_STRUCTURES.map((s) => (
                        <SelectItem key={s} value={s} data-testid={`site-form-rate-structure-option-${s}`}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="site-form-cancel-button">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} data-testid="site-form-submit-button" className="bg-amber-600 hover:bg-amber-700">
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Create Site"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
